import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Calendar, Clock, MapPin, User, CreditCard, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { fr, enUS, ar } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { t, language, dir } = useLanguage();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBookings, setExpandedBookings] = useState({});

  const locales = { fr, en: enUS, ar };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?mode=login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (token) {
      fetchBookings();
    }
  }, [token]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || colors.pending;
  };

  const getPaymentBadge = (status) => {
    const colors = {
      pending: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colors[status] || colors.pending;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const toggleBookingExpand = (bookingId) => {
    setExpandedBookings(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background-light flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light pt-24 pb-16" dir={dir}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading text-3xl md:text-4xl font-light text-text-primary mb-2" data-testid="dashboard-title">
            {t('dashboard.title')}
          </h1>
          <p className="font-body text-text-muted">
            {t('dashboard.welcome')}, <span className="text-primary">{user?.name}</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-white p-6 border border-primary/10">
            <p className="font-body text-sm text-text-muted mb-1">Total</p>
            <p className="font-heading text-3xl font-light text-text-primary">{bookings.length}</p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <p className="font-body text-sm text-text-muted mb-1">{t('status.pending')}</p>
            <p className="font-heading text-3xl font-light text-yellow-600">
              {bookings.filter((b) => b.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <p className="font-body text-sm text-text-muted mb-1">{t('status.confirmed')}</p>
            <p className="font-heading text-3xl font-light text-green-600">
              {bookings.filter((b) => b.status === 'confirmed').length}
            </p>
          </div>
          <div className="bg-white p-6 border border-primary/10">
            <p className="font-body text-sm text-text-muted mb-1">
              {language === 'ar' ? 'ردود' : 'Réponses'}
            </p>
            <p className="font-heading text-3xl font-light text-purple-600">
              {bookings.filter((b) => b.admin_response).length}
            </p>
          </div>
        </div>

        {/* Bookings List */}
        <div>
          <h2 className="font-heading text-2xl font-light text-text-primary mb-6">
            {t('dashboard.myBookings')}
          </h2>

          {bookings.length === 0 ? (
            <div className="bg-white p-12 border border-primary/10 text-center">
              <Calendar className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <p className="font-body text-text-muted">{t('dashboard.noBookings')}</p>
              <Link
                to="/booking"
                className="btn-primary mt-6 inline-block"
              >
                {t('nav.book')}
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-white border border-primary/10 overflow-hidden"
                  data-testid={`booking-item-${booking.id}`}
                >
                  {/* Booking Header */}
                  <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="font-body text-sm font-medium text-primary">
                          {booking.reference}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-body ${getStatusBadge(booking.status)}`}>
                          {t(`status.${booking.status}`)}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-body ${getPaymentBadge(booking.payment_status)}`}>
                          {booking.payment_status === 'paid' ? '✓ Payé' : 'En attente'}
                        </span>
                        {booking.admin_response && (
                          <span className="px-2 py-0.5 text-xs font-body bg-purple-100 text-purple-800 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {language === 'ar' ? 'رد جديد' : 'Réponse reçue'}
                          </span>
                        )}
                      </div>

                      <h3 className="font-heading text-lg font-medium text-text-primary mb-3">
                        {booking.event_type}
                        {booking.sub_service_type && ` - ${booking.sub_service_type}`}
                      </h3>

                      <div className="flex flex-wrap gap-4 text-sm text-text-muted">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(booking.event_date), 'dd MMM yyyy', {
                            locale: locales[language] || locales.fr,
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {booking.location}
                        </span>
                        {booking.guest_count && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {booking.guest_count} {language === 'ar' ? 'ضيف' : 'invités'}
                          </span>
                        )}
                        {booking.rental_duration_days && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {booking.rental_duration_days} {language === 'ar' ? 'أيام' : 'jours'}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-body text-sm text-text-muted">{t('dashboard.amount')}</p>
                        <p className="font-heading text-xl text-primary">
                          {formatPrice(booking.total_amount || 0)} XAF
                        </p>
                      </div>
                      
                      <button
                        onClick={() => toggleBookingExpand(booking.id)}
                        className="p-2 border border-primary/20 hover:bg-primary/5 transition-colors"
                      >
                        {expandedBookings[booking.id] ? (
                          <ChevronUp className="w-5 h-5 text-text-muted" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-text-muted" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedBookings[booking.id] && (
                    <div className="border-t border-primary/10 p-6 bg-surface-light">
                      {/* Admin Response */}
                      {booking.admin_response && (
                        <div className="mb-6 p-4 bg-white border-l-4 border-primary">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-primary" />
                            <span className="font-body text-sm font-medium text-primary">
                              {language === 'ar' ? 'رد من نور إيفنتس' : 'Réponse de Nûr Events'}
                            </span>
                          </div>
                          <p className="font-body text-sm text-text-primary whitespace-pre-line">
                            {booking.admin_response}
                          </p>
                          <p className="font-body text-xs text-text-muted mt-2">
                            {formatDateTime(booking.admin_responded_at)}
                          </p>
                        </div>
                      )}

                      {/* Booking Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {booking.vehicle_type && (
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">
                              {language === 'ar' ? 'نوع السيارة' : 'Véhicule'}
                            </p>
                            <p className="font-body text-sm text-text-primary">{booking.vehicle_type}</p>
                          </div>
                        )}
                        {booking.venue_type && (
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">
                              {language === 'ar' ? 'نوع القاعة' : 'Type de salle'}
                            </p>
                            <p className="font-body text-sm text-text-primary">{booking.venue_type}</p>
                          </div>
                        )}
                        {booking.budget_range && (
                          <div>
                            <p className="font-body text-xs text-text-muted mb-1">
                              {language === 'ar' ? 'الميزانية' : 'Budget'}
                            </p>
                            <p className="font-body text-sm text-text-primary">{booking.budget_range}</p>
                          </div>
                        )}
                        {booking.notes && (
                          <div className="md:col-span-2">
                            <p className="font-body text-xs text-text-muted mb-1">
                              {language === 'ar' ? 'ملاحظاتك' : 'Vos notes'}
                            </p>
                            <p className="font-body text-sm text-text-primary">{booking.notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      {booking.payment_status !== 'paid' && (
                        <div className="mt-6 pt-4 border-t border-primary/10">
                          <Link
                            to={`/booking?service=${booking.service_id}`}
                            className="btn-outline text-sm inline-flex items-center gap-2"
                          >
                            <CreditCard className="w-4 h-4" />
                            {language === 'ar' ? 'إتمام الدفع' : 'Compléter le paiement'}
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
